import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Crown, X, ChevronDown, ChevronUp, Search, UserPlus, Pencil } from 'lucide-react';

interface CareTeamMember {
  coach_id: string;
  name: string;
  email: string;
  role: 'primary' | 'delegated' | 'secondary';
  assigned_at: string;
}

interface StaffMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface CareTeamTileProps {
  clientId: string;
  onTeamChange?: () => void;
  canManageTeam?: boolean;  // Only primary coach or admin can add/remove/promote/delegate
}

const CareTeamTile: React.FC<CareTeamTileProps> = ({ clientId, onTeamChange, canManageTeam = false }) => {
  const [careTeam, setCareTeam] = useState<CareTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('careTeamTileCollapsed') === 'true';
  });
  const addRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
  });

  const loadCareTeam = useCallback(async () => {
    try {
      const response = await fetch(`/api/coach/clients/${clientId}/assigned-coach`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCareTeam(data.care_team || []);
      }
    } catch (error) {
      console.error('[CareTeamTile] Error loading care team:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadCareTeam();
  }, [loadCareTeam]);

  // Close dropdown on outside click or Escape
  useEffect(() => {
    if (!addOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (addRef.current && !addRef.current.contains(e.target as Node)) {
        setAddOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAddOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [addOpen]);

  // Focus search input when add dropdown opens
  useEffect(() => {
    if (addOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [addOpen]);

  useEffect(() => {
    localStorage.setItem('careTeamTileCollapsed', String(collapsed));
  }, [collapsed]);

  const loadStaffList = async () => {
    setStaffLoading(true);
    try {
      const response = await fetch('/api/staff/list', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStaffList(data.staff || []);
      }
    } catch (error) {
      console.error('[CareTeamTile] Error loading staff:', error);
    } finally {
      setStaffLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setAddOpen(true);
    setSearchQuery('');
    loadStaffList();
  };

  const handleAddMember = async (staffId: string) => {
    try {
      const response = await fetch(`/api/coach/clients/${clientId}/assign`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ coach_id: staffId, role: 'secondary' })
      });
      if (response.ok) {
        setAddOpen(false);
        await loadCareTeam();
        onTeamChange?.();
      }
    } catch (error) {
      console.error('[CareTeamTile] Error adding team member:', error);
    }
  };

  const handleRemoveMember = async (coachId: string) => {
    try {
      const response = await fetch(`/api/coach/clients/${clientId}/assign/${coachId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        await loadCareTeam();
        onTeamChange?.();
      }
    } catch (error) {
      console.error('[CareTeamTile] Error removing team member:', error);
    }
  };

  const handlePromoteToPrimary = async (coachId: string) => {
    try {
      const response = await fetch(`/api/coach/clients/${clientId}/care-team/${coachId}/role`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ role: 'primary' })
      });
      if (response.ok) {
        await loadCareTeam();
        onTeamChange?.();
      }
    } catch (error) {
      console.error('[CareTeamTile] Error promoting member:', error);
    }
  };

  const handleToggleDelegate = async (coachId: string, currentRole: string) => {
    const newRole = currentRole === 'delegated' ? 'secondary' : 'delegated';
    try {
      const response = await fetch(`/api/coach/clients/${clientId}/care-team/${coachId}/role`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ role: newRole })
      });
      if (response.ok) {
        await loadCareTeam();
        onTeamChange?.();
      }
    } catch (error) {
      console.error('[CareTeamTile] Error toggling delegate:', error);
    }
  };

  const staffDisplayName = (s: StaffMember) =>
    `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email;

  const staffInitials = (s: StaffMember) => {
    const name = staffDisplayName(s);
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  // Filter staff not already on the team
  const teamIds = new Set(careTeam.map(m => m.coach_id));
  const availableStaff = staffList
    .filter(s => !teamIds.has(s.id))
    .filter(s => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      const fullName = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
      return fullName.includes(q) || s.email.toLowerCase().includes(q);
    });

  const primary = careTeam.find(m => m.role === 'primary');
  const secondaries = careTeam.filter(m => m.role !== 'primary');

  const titleBar = (
    <div className="ct-header" onClick={() => setCollapsed(!collapsed)}>
      <h3 className="ct-title"><UserPlus size={14} /> Care Team</h3>
      <button className="ct-collapse-btn" title={collapsed ? 'Expand' : 'Collapse'}>
        {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="card care-team-tile">
        {titleBar}
        {!collapsed && <div className="ct-loading">Loading...</div>}
        <style>{styles}</style>
      </div>
    );
  }

  if (careTeam.length === 0) {
    return (
      <div className="card care-team-tile">
        {titleBar}
        {!collapsed && (
          <>
            <div className="ct-empty">No coaches assigned</div>
            {canManageTeam && (
              <div className="ct-add-wrapper" ref={addRef}>
                <button className="ct-add-btn" onClick={handleOpenAdd}>
                  + Add Team Member
                  <ChevronDown size={12} />
                </button>
                {addOpen && renderAddDropdown()}
              </div>
            )}
          </>
        )}
        <style>{styles}</style>
      </div>
    );
  }

  function renderAddDropdown() {
    return (
      <div className="ct-add-dropdown">
        <div className="ct-search-wrapper">
          <Search size={12} />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search staff..."
            className="ct-search-input"
          />
        </div>
        <div className="ct-staff-list">
          {staffLoading ? (
            <div className="ct-staff-item ct-staff-loading">Loading staff...</div>
          ) : availableStaff.length === 0 ? (
            <div className="ct-staff-item ct-staff-empty">
              {searchQuery ? 'No matches' : 'All staff already assigned'}
            </div>
          ) : (
            availableStaff.map(staff => (
              <button
                key={staff.id}
                className="ct-staff-item"
                onClick={() => handleAddMember(staff.id)}
              >
                <div className="ct-staff-avatar">
                  {staffInitials(staff)}
                </div>
                <div className="ct-staff-info">
                  <span className="ct-staff-name">{staffDisplayName(staff)}</span>
                  <span className="ct-staff-email">{staff.email}</span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="card care-team-tile">
      {titleBar}

      {!collapsed && <>
      {/* Primary Coach */}
      {primary && (
        <div className="ct-section">
          <div className="ct-label">PRIMARY</div>
          <div className="ct-member ct-primary">
            <div className="ct-member-avatar">
              {primary.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </div>
            <div className="ct-member-info">
              <span className="ct-member-name">{primary.name}</span>
            </div>
            <Crown size={14} className="ct-crown" />
          </div>
        </div>
      )}

      {/* Secondary & Delegated Staff */}
      {secondaries.length > 0 && (
        <div className="ct-section">
          <div className="ct-label">SECONDARY</div>
          {secondaries.map(member => (
            <div key={member.coach_id} className={`ct-member ${member.role === 'delegated' ? 'ct-delegated' : 'ct-secondary'}`}>
              <div className="ct-member-avatar">
                {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div className="ct-member-info">
                <span className="ct-member-name">{member.name}</span>
              </div>
              {/* Silver crown — always visible for delegated, hides on hover */}
              {member.role === 'delegated' && (
                <Crown size={14} className="ct-crown-silver" />
              )}
              {/* Action buttons — visible on hover, only for primary/admin */}
              {canManageTeam && (
                <div className="ct-member-actions">
                  <button
                    className={`ct-action-btn ct-delegate ${member.role === 'delegated' ? 'ct-delegate-active' : ''}`}
                    onClick={() => handleToggleDelegate(member.coach_id, member.role)}
                    title={member.role === 'delegated' ? 'Revoke Delegation' : 'Delegate Write Access'}
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    className="ct-action-btn ct-promote"
                    onClick={() => handlePromoteToPrimary(member.coach_id)}
                    title="Make Primary"
                  >
                    <Crown size={12} />
                  </button>
                  <button
                    className="ct-action-btn ct-remove"
                    onClick={() => handleRemoveMember(member.coach_id)}
                    title="Remove from team"
                  >
                    <X size={12} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Team Member — only for primary coach or admin */}
      {canManageTeam && (
        <div className="ct-add-wrapper" ref={addRef}>
          <button className="ct-add-btn" onClick={handleOpenAdd}>
            + Add Team Member
            <ChevronDown size={12} />
          </button>
          {addOpen && renderAddDropdown()}
        </div>
      )}
      </>}

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .care-team-tile {
    padding: 14px;
    overflow: visible !important;
    position: relative;
  }

  .ct-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    margin-bottom: 10px;
  }

  .ct-header:only-child {
    margin-bottom: 0;
  }

  .ct-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
  }

  .ct-title svg {
    color: var(--accent-green);
  }

  .ct-collapse-btn {
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
  }

  .ct-collapse-btn:hover {
    color: var(--text-primary);
  }

  .ct-loading, .ct-empty {
    font-size: 12px;
    color: var(--text-secondary);
    padding: 8px 0;
  }

  .ct-section {
    margin-bottom: 8px;
  }

  .ct-label {
    font-size: 10px;
    font-weight: 700;
    color: var(--text-tertiary);
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }

  .ct-member {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    transition: background 0.1s ease;
  }

  .ct-member:hover {
    background: var(--bg-primary);
  }

  .ct-member-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--accent-green);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .ct-secondary .ct-member-avatar {
    background: var(--text-tertiary);
  }

  .ct-delegated .ct-member-avatar {
    background: #6b7280;
  }

  .ct-member-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .ct-member-name {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ct-member-email {
    font-size: 10px;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ct-crown {
    color: #f59e0b;
    flex-shrink: 0;
  }

  .ct-crown-silver {
    color: #9ca3af;
    flex-shrink: 0;
  }

  .ct-member:hover .ct-crown-silver {
    display: none;
  }

  .ct-member-actions {
    display: none;
    gap: 4px;
  }

  .ct-member:hover .ct-member-actions {
    display: flex;
  }

  .ct-action-btn {
    width: 22px;
    height: 22px;
    border: none;
    border-radius: 4px;
    background: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.1s ease;
  }

  .ct-promote {
    color: var(--text-secondary);
  }

  .ct-promote:hover {
    color: #f59e0b;
    background: rgba(245, 158, 11, 0.1);
  }

  .ct-remove {
    color: var(--text-secondary);
  }

  .ct-remove:hover {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
  }

  .ct-delegate {
    color: var(--text-secondary);
  }

  .ct-delegate:hover {
    color: #60a5fa;
    background: rgba(96, 165, 250, 0.1);
  }

  .ct-delegate-active {
    color: #60a5fa;
    opacity: 1 !important;
  }

  .ct-delegate-active:hover {
    color: var(--text-secondary);
    background: rgba(156, 163, 175, 0.1);
  }

  /* Add Team Member */
  .ct-add-wrapper {
    position: relative;
    margin-top: 6px;
  }

  .ct-add-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 12px;
    background: none;
    border: 1px dashed var(--border-secondary);
    border-radius: 6px;
    font-size: 12px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .ct-add-btn:hover {
    border-color: var(--accent-green);
    color: var(--accent-green);
  }

  .ct-add-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: var(--bg-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: 8px;
    z-index: 100;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    max-height: 240px;
    display: flex;
    flex-direction: column;
  }

  .ct-search-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    border-bottom: 1px solid var(--border-secondary);
    color: var(--text-secondary);
  }

  .ct-search-input {
    flex: 1;
    background: none;
    border: none;
    font-size: 12px;
    color: var(--text-primary);
    outline: none;
  }

  .ct-search-input::placeholder {
    color: var(--text-tertiary);
  }

  .ct-staff-list {
    overflow-y: auto;
    max-height: 180px;
  }

  .ct-staff-item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    background: none;
    border: none;
    cursor: pointer;
    transition: background 0.1s ease;
    text-align: left;
  }

  .ct-staff-item:hover {
    background: var(--bg-primary);
  }

  .ct-staff-loading, .ct-staff-empty {
    font-size: 12px;
    color: var(--text-secondary);
    cursor: default;
    justify-content: center;
  }

  .ct-staff-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--text-tertiary);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .ct-staff-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .ct-staff-name {
    font-size: 12px;
    color: var(--text-primary);
  }

  .ct-staff-email {
    font-size: 10px;
    color: var(--text-secondary);
  }
`;

export default CareTeamTile;
